// PermissionInspector.jsx — v149 فاحص الصلاحيات: ماذا يرى الموظف بالضبط؟
import React, { useEffect, useMemo, useState } from "react";
import { api } from "./lib/api";
import { ALL_TABS, canTab } from "./tabs";
import { zVar } from "./lib/zIndex";

const Chip = ({ ok, children }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 4, margin: 3,
    padding: "4px 10px", borderRadius: 16, fontSize: 11.5, fontWeight: 700,
    background: ok ? "#DCFCE7" : "#FEE2E2", color: ok ? "#166534" : "#991B1B",
  }}>
    {ok ? "✅" : "🚫"} {children}
  </span>
);

// خصائص حساسة/رئيسية تُعرض بشكل بارز (مطابقة لشروط الواجهة الفعلية)
const HIGHLIGHTS = [
  { label: "زر ولوحة WhatsApp", test: (role, has) => role === "admin" || role === "ceo" || has("whatsapp.send") || has("whatsapp.chat") || has("whatsapp.full") },
  { label: "إدارة أرقام جوال المدراء (خاصية خاصة)", test: (role, has) => role === "admin" || has("whatsapp.admin_phones") || has("whatsapp.full") },
  { label: "سجل تدقيق WhatsApp", test: (role, has) => ["admin", "ceo"].includes(role) || has("whatsapp.audit") || has("system.audit") || has("whatsapp.full") },
  { label: "جدولة الحملات", test: (role, has) => role === "admin" || has("marketing.campaigns_manage") },
  { label: "غرفة القيادة التنفيذية", test: (role, has) => role === "admin" || role === "ceo" || has("ceo.view") },
  { label: "مركز المستخدمين وإدارة الصلاحيات", test: (role) => role === "admin" },
];

export default function PermissionInspector({ userId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [openGroups, setOpenGroups] = useState({});

  useEffect(() => {
    api.get(`/rbac/inspect/${userId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "تعذّر جلب الصلاحيات"));
  }, [userId]);

  const perms = useMemo(() => new Set(data?.effective || []), [data]);
  const has = (k) => perms.has(k);
  const role = (data?.user?.role || "").toLowerCase();

  const tabs = useMemo(() => {
    if (!data) return [];
    const simUser = { role: data.user.role };
    return ALL_TABS.filter((t) => !t.hidden).map((t) => ({
      id: t.id, label: t.label, visible: canTab(t, simUser, has),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: zVar('extra_1200'), display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div dir="rtl" style={{ background: "#fff", borderRadius: 16, maxWidth: 700, width: "100%", maxHeight: "88vh", overflowY: "auto", padding: 20 }} onClick={(e) => e.stopPropagation()} data-testid="permission-inspector">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 16, fontWeight: 900, color: "#231709" }}>🔍 فاحص الصلاحيات</div>
          <button onClick={onClose} data-testid="inspector-close-btn" style={{ border: "none", background: "#f3f4f6", borderRadius: 8, padding: "4px 12px", cursor: "pointer", fontWeight: 700 }}>✕</button>
        </div>
        {err && <div style={{ color: "#b91c1c", fontSize: 13 }}>{err}</div>}
        {!data && !err && <div style={{ fontSize: 13, color: "#8a6d4d", padding: 20, textAlign: "center" }}>⏳ جاري الفحص…</div>}
        {data && (
          <>
            <div style={{ fontSize: 12.5, color: "#6b5138", marginBottom: 14 }}>
              {data.user.name || data.user.username} · الدور: <b>{data.user.role}</b> · الصلاحيات الفعلية: <b>{data.effective.length}</b>
            </div>

            <div style={{ fontSize: 13.5, fontWeight: 800, color: "#5c3a1e", marginBottom: 6 }}>⭐ الخصائص الرئيسية</div>
            <div style={{ marginBottom: 14 }} data-testid="inspector-highlights">
              {HIGHLIGHTS.map((h, i) => <Chip key={i} ok={h.test(role, has)}>{h.label}</Chip>)}
            </div>

            <div style={{ fontSize: 13.5, fontWeight: 800, color: "#5c3a1e", marginBottom: 6 }}>
              🗂️ التبويبات ({tabs.filter((t) => t.visible).length} ظاهر / {tabs.filter((t) => !t.visible).length} مخفي)
            </div>
            <div style={{ marginBottom: 14 }} data-testid="inspector-tabs">
              {tabs.map((t) => <Chip key={t.id} ok={t.visible}>{t.label}</Chip>)}
            </div>

            <div style={{ fontSize: 13.5, fontWeight: 800, color: "#5c3a1e", marginBottom: 6 }}>🔑 الصلاحيات التفصيلية بالمجموعات</div>
            {data.groups.map((g) => {
              const on = g.items.filter((i) => i.granted).length;
              const open = !!openGroups[g.name];
              return (
                <div key={g.name} style={{ border: "1px solid #e6d9b8", borderRadius: 10, marginBottom: 6 }}>
                  <button onClick={() => setOpenGroups((o) => ({ ...o, [g.name]: !open }))}
                          data-testid={`inspector-group-${g.name}`}
                          style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", background: "none", border: "none", padding: "8px 12px", cursor: "pointer" }}>
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: "#231709" }}>{g.name}</span>
                    <span style={{ fontSize: 11.5, color: on ? "#166534" : "#991B1B", fontWeight: 700 }}>
                      {on}/{g.items.length} مفعّلة {open ? "▲" : "▼"}
                    </span>
                  </button>
                  {open && (
                    <div style={{ padding: "0 12px 10px" }}>
                      {g.items.map((i) => <Chip key={i.key} ok={i.granted}>{i.label}</Chip>)}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
